import type {ReactElement} from 'react';
import React, {useState, useEffect} from 'react';
import type {DragDirection, Range, ResizeEndEvent, Span} from '@Libraries/timeline/types';
import {getCurrentPageDuration} from '@Components/poster-editor/poster-editor-reducer';
import {DesignSeekItemBackgroundContainer} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/design-seek-item-background-container/design-seek-item-background-container';
import {BaseTimelineItem} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/base-timeline-item/base-timeline-item';
import {
  updateDesignItemSelectionState,
  updateDuplicateDuration,
  updateDuplicateMaxDuration,
  updateIsDesignItemResizing,
} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/poster-editor-web-bottom-bar.reducer';
import {POSTER_MAX_DURATION, POSTER_MIN_DURATION} from '@PosterWhiteboard/poster/poster.types';
import type {TimelineDesignSeekItemProps} from './timeline-design-seek-item.types';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {useGetPosterMinDuration} from '@Hooks/poster-editor/useGetPosterMinDuration';

export function TimelineDesignSeekItem({...props}: TimelineDesignSeekItemProps): ReactElement {
  const currentPageDuration = useAppSelector(getCurrentPageDuration);
  const minDuration = useGetPosterMinDuration();
  const dispatch = useAppDispatch();
  const isSelected = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.isDesignItemSelected;
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [rangeOnResize, setRange] = useState(props.range);

  useEffect((): void => {
    dispatch(updateDuplicateDuration(rangeOnResize.end - rangeOnResize.start));
  }, [rangeOnResize]);

  const onResizeEnd = async (event: ResizeEndEvent): Promise<void> => {
    const updatedRange = event.active.data.current.getSpanFromResizeEvent?.(event);
    if (!updatedRange) {
      return;
    }

    await updateOnResize(updatedRange, true);
    await window.posterEditor?.whiteboard?.audioClips.trimAudiosExceedingPageDuration();
    await window.posterEditor?.whiteboard?.getCurrentPage().items.trimAllTranscriptItemsExceedingPageDuration();
    dispatch(updateIsDesignItemResizing(false));
    dispatch(updateDuplicateMaxDuration(updatedRange.end - updatedRange.start));
    setTimeout((): void => {
      setIsUpdating(false);
    }, 300);
  };

  const updateOnResize = async (range: Range, undoable = false): Promise<void> => {
    const newDuration = range.end - range.start;
    const minDuration = window.posterEditor?.whiteboard?.getCurrentPage().items.getMaxAnimationTime() ?? POSTER_MIN_DURATION;

    await window.posterEditor?.whiteboard?.getCurrentPage()?.updateFromObject(
      {
        duration: Math.min(POSTER_MAX_DURATION, Math.max(minDuration, Number(newDuration.toFixed(2)))),
      },
      {
        undoable,
      }
    );
  };

  const getBackgroundImageContainer = (): ReactElement => {
    return <DesignSeekItemBackgroundContainer />;
  };

  const getTimeInSeconds = (): number => {
    return currentPageDuration ?? 0;
  };

  const onClickDesignItem = (): void => {
    if (!isSelected) {
      window.posterEditor?.whiteboard?.audioClips.selectAudioItem('');
      window.posterEditor?.whiteboard?.getCurrentPage().activeSelection.clearSelection();
      dispatch(updateDesignItemSelectionState(true));
    }
  };

  const onResizeStart = (): void => {
    setRange(props.range);
    setIsUpdating(true);
    dispatch(updateDuplicateDuration(props.range.end - props.range.start));
    dispatch(updateIsDesignItemResizing(true));
  };

  const onResizeMove = async (updatedRange: Span, direction: DragDirection): Promise<void> => {
    if (direction === 'start' && updatedRange.start < 0) {
      return;
    }

    if (updatedRange.end - updatedRange.start > 600) {
      return;
    }

    setRange(updatedRange);
  };

  const onAutoScroll = (increment: number, direction: DragDirection = 'end'): void => {
    if (direction === 'start') {
      setRange((oldRange) => {
        return {
          start: oldRange.start + increment,
          end: oldRange.end,
        };
      });
      return;
    }

    setRange((oldRange) => {
      return {
        start: oldRange.start,
        end: oldRange.end + increment,
      };
    });
  };

  return (
    <BaseTimelineItem
      id={props.id}
      key={props.id}
      range={isUpdating ? rangeOnResize : props.range}
      duration={getTimeInSeconds()}
      onResizeStart={onResizeStart}
      onResizeMove={(updatedRangeValueOnResize, direction): void => {
        void onResizeMove(updatedRangeValueOnResize, direction);
      }}
      onResizeEnd={(e): void => {
        void onResizeEnd(e);
      }}
      onAutoScroll={onAutoScroll}
      onClick={onClickDesignItem}
      isSelected={isSelected}
      minDuration={minDuration}
      maxDuration={POSTER_MAX_DURATION}
      disableDragOnly
    >
      {getBackgroundImageContainer()}
    </BaseTimelineItem>
  );
}
